import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useI18n } from '../../i18n';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useEmail } from '../../context/EmailContext';
import { useEmailComposer } from '../../hooks/useEmailComposer';
import { notify } from '../CustomToast';
import { FaTimes, FaPaperclip, FaPaperPlane, FaSave } from 'react-icons/fa';
import EmailAttachmentChip from './EmailAttachmentChip';
import './EmailSection.css'; // All styles are now here

/**
 * Enhanced Email Composer
 * - Standalone CSS in EmailSection.css (no ProjectComposer.css dependency)
 * - Rich Text Editor (Quill)
 * - Per-file attachment upload with progress
 */
const EmailComposer = ({ isOpen, onClose, mode = 'compose', originalEmail = null }) => {
    const { t } = useI18n();
    const { activeAccountId, accounts, setActiveAccountId, isLoading: isContextLoading, activeAccount } = useEmail();
    const { sendEmail, saveDraft, replyEmail, forwardEmail, sendExistingDraft, isSending, uploadTempFile } = useEmailComposer(activeAccountId);

    const [to, setTo] = useState('');
    const [cc, setCc] = useState('');
    const [bcc, setBcc] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [body, setBody] = useState('');
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [errors, setErrors] = useState({});

    const clearError = (field) => {
        if (errors[field] || errors[`${field}.0`]) {
            setErrors(prev => {
                const e = { ...prev };
                delete e[field];
                delete e[`${field}.0`];
                return e;
            });
        }
    };

    // Attachments state: array of objects { file, id, progress, error, controller }
    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef(null);

    // Quill ref to focus editor
    const quillRef = useRef(null);

    // Ensure an account is selected if available
    useEffect(() => {
        if (isOpen && !activeAccountId && accounts.length > 0) {
            const defaultAccount = accounts.find(a => a.is_default) || accounts[0];
            if (defaultAccount) {
                setActiveAccountId(defaultAccount.id);
            }
        }
    }, [isOpen, activeAccountId, accounts, setActiveAccountId]);

    // Build quoted-reply HTML block from the original email
    const buildQuotedBody = (email) => {
        if (!email) return '';
        const date = email.date ? new Date(email.date).toLocaleString() : '';
        const senderName = email.from?.name || email.from?.email || '';
        const senderEmail = email.from?.email || '';
        const originalBody = email.body_html
            || (email.body_text ? email.body_text.replace(/\n/g, '<br>') : '');
        return `<br><br><div style="border-left:2px solid #ccc;padding-left:12px;margin-left:4px;color:#555;font-size:13px"><p style="margin:0 0 8px">On ${date}, ${senderName} &lt;${senderEmail}&gt; wrote:</p>${originalBody}</div>`;
    };

    const recipientsToString = (arr) => {
        if (!arr || arr.length === 0) return '';
        return arr.map(r => r.email || r).join(', ');
    };

    // Initialize fields based on mode whenever the modal opens
    useEffect(() => {
        if (!isOpen) return;

        setBcc('');
        setAttachments([]);
        setShowBcc(false);
        setErrors({});

        if (mode === 'reply') {
            setTo(originalEmail?.from?.email || '');
            setCc('');
            setShowCc(false);
            const subj = originalEmail?.subject || '';
            setEmailSubject(subj.startsWith('Re:') ? subj : `Re: ${subj}`);
            setBody(buildQuotedBody(originalEmail));
        } else if (mode === 'replyAll') {
            setTo(originalEmail?.from?.email || '');
            const ownEmail = activeAccount?.email;
            const toStr = recipientsToString(originalEmail?.to?.filter(r => (r.email || r) !== ownEmail));
            const ccStr = recipientsToString(originalEmail?.cc?.filter(r => (r.email || r) !== ownEmail));
            const combined = [toStr, ccStr].filter(Boolean).join(', ');
            setCc(combined);
            setShowCc(!!combined);
            const subj = originalEmail?.subject || '';
            setEmailSubject(subj.startsWith('Re:') ? subj : `Re: ${subj}`);
            setBody(buildQuotedBody(originalEmail));
        } else if (mode === 'forward') {
            setTo('');
            setCc('');
            setShowCc(false);
            const subj = originalEmail?.subject || '';
            setEmailSubject(subj.startsWith('Fwd:') ? subj : `Fwd: ${subj}`);
            setBody(buildQuotedBody(originalEmail));
        } else if (mode === 'draft') {
            const toStr = recipientsToString(originalEmail?.to);
            const ccStr = recipientsToString(originalEmail?.cc);
            setTo(toStr);
            setCc(ccStr);
            setShowCc(!!ccStr);
            setEmailSubject(originalEmail?.subject || '');
            setBody(originalEmail?.body_html || originalEmail?.body_text || '');
        } else {
            // compose
            setTo('');
            setCc('');
            setShowCc(false);
            setEmailSubject('');
            setBody('');
        }
    }, [isOpen, mode, originalEmail]);

    // Handle file selection
    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Create attachment objects
        const newAttachments = files.map(file => ({
            file,
            id: null, // temp upload id from backend
            progress: 0,
            error: null,
            controller: new AbortController(), // for cancellation
            tempId: Math.random().toString(36).substr(2, 9) // local unique id for UI key
        }));

        setAttachments(prev => [...prev, ...newAttachments]);

        // Reset input so same file can be selected again if needed
        e.target.value = '';

        // Start uploads
        newAttachments.forEach(att => uploadAttachment(att));
    };

    const uploadAttachment = async (attachment) => {
        const result = await uploadTempFile(attachment.file, (progress) => {
            setAttachments(prev => prev.map(a =>
                a.tempId === attachment.tempId ? { ...a, progress } : a
            ));
        }, attachment.controller.signal);

        if (result && result.id) {
            setAttachments(prev => prev.map(a =>
                a.tempId === attachment.tempId ? { ...a, id: result.id, progress: 100 } : a
            ));
        } else {
            setAttachments(prev => prev.map(a =>
                a.tempId === attachment.tempId ? { ...a, error: 'Upload failed', progress: 0 } : a
            ));
        }
    };

    const removeAttachment = (attachment) => {
        // Abort upload if in progress
        if (attachment.controller) {
            attachment.controller.abort();
        }
        setAttachments(prev => prev.filter(a => a.tempId !== attachment.tempId));
    };

    const parseRecipients = (str) => {
        if (!str || typeof str !== 'string') return [];
        return str.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    };

    // True when any attachment is still uploading (id not yet assigned, not errored)
    const isUploading = attachments.some(a => a.id === null && !a.error && a.progress > 0);

    const handleSend = async () => {
        setErrors({});

        // Client-side validation — instant feedback before network round-trip
        if (mode === 'compose' || mode === 'forward') {
            if (parseRecipients(to).length === 0) {
                notify('error', 'Please add at least one recipient');
                return;
            }
        }
        const bodyText = body.replace(/<[^>]+>/g, '').trim();
        if (!bodyText) {
            notify('error', 'Email body cannot be empty');
            return;
        }

        const validAttachmentIds = attachments
            .filter(a => a.id !== null && !a.error)
            .map(a => a.id);

        const data = {
            to: parseRecipients(to),
            cc: parseRecipients(cc),
            bcc: parseRecipients(bcc),
            subject: emailSubject,
            body,
            temp_upload_ids: validAttachmentIds,
        };

        try {
            let result;
            if (mode === 'reply') {
                result = await replyEmail(originalEmail.id, data, false);
            } else if (mode === 'replyAll') {
                result = await replyEmail(originalEmail.id, data, true);
            } else if (mode === 'forward') {
                result = await forwardEmail(originalEmail.id, data);
            } else if (mode === 'draft' && originalEmail?.id) {
                // H9: Update and send the existing draft instead of creating a new email
                result = await sendExistingDraft(originalEmail.id, data);
            } else {
                result = await sendEmail(data);
            }
            if (result) onClose();
        } catch (error) {
            if (error?.status === 422) {
                setErrors(error.errors || {});
            }
        }
    };

    const handleDraft = async () => {
        setErrors({});
        const validAttachmentIds = attachments
            .filter(a => a.id !== null && !a.error)
            .map(a => a.id);

        try {
            const result = await saveDraft({
                to: parseRecipients(to),
                cc: parseRecipients(cc),
                bcc: parseRecipients(bcc),
                subject: emailSubject,
                body,
                temp_upload_ids: validAttachmentIds
            });
            if (result) onClose();
        } catch (error) {
            if (error?.status === 422) {
                setErrors(error.errors || {});
            }
        }
    };

    if (!isOpen) return null;

    // Quill modules
    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'clean']
        ],
    };

    // Explicitly whitelist formats to avoid "Cannot register bullet" error
    // 'bullet' is a value for 'list', not a format itself.
    const formats = [
        'bold', 'italic', 'underline', 'strike',
        'list', 'link'
    ];

    return ReactDOM.createPortal(
        <div className="email-composer-overlay">
            <div className="email-composer-modal">
                {/* Header */}
                <div className="email-composer-header">
                    <h2 className="email-composer-title">
                        {mode === 'reply' && t('pages.email.compose.title_reply')}
                        {mode === 'replyAll' && t('pages.email.compose.title_reply_all')}
                        {mode === 'forward' && t('pages.email.compose.title_forward')}
                        {mode === 'compose' && t('pages.email.compose.title_compose')}
                        {mode === 'draft' && t('pages.email.compose.title_draft')}
                    </h2>
                    <button onClick={onClose} className="email-composer-close" aria-label="Close">
                        <FaTimes />
                    </button>
                </div>

                {/* Recipient Fields */}
                <div className="email-composer-fields">
                    {/* To */}
                    <div className="email-composer-field-row">
                        <label className="email-field-label">{t('pages.email.compose.to')}</label>
                        <div className="email-field-input-wrap">
                            <input
                                type="text"
                                className={`email-field-input ${(errors.to || errors['to.0']) ? 'is-invalid' : ''}`}
                                value={to}
                                onChange={(e) => { setTo(e.target.value); clearError('to'); }}
                                placeholder={t('pages.email.compose.recipient_placeholder')}
                            />
                            {(errors.to || errors['to.0']) && (
                                <div className="field-error">{(errors.to || errors['to.0'])[0]}</div>
                            )}
                        </div>
                        <div className="email-field-toggles">
                            {!showCc && <button onClick={() => setShowCc(true)}>Cc</button>}
                            {!showBcc && <button onClick={() => setShowBcc(true)}>Bcc</button>}
                        </div>
                    </div>

                    {/* Cc */}
                    {showCc && (
                        <div className="email-composer-field-row">
                            <label className="email-field-label">{t('pages.email.compose.cc')}</label>
                            <div className="email-field-input-wrap">
                                <input
                                    type="text"
                                    className={`email-field-input ${(errors.cc || errors['cc.0']) ? 'is-invalid' : ''}`}
                                    value={cc}
                                    onChange={(e) => { setCc(e.target.value); clearError('cc'); }}
                                />
                                {(errors.cc || errors['cc.0']) && (
                                    <div className="field-error">{(errors.cc || errors['cc.0'])[0]}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Bcc */}
                    {showBcc && (
                        <div className="email-composer-field-row">
                            <label className="email-field-label">{t('pages.email.compose.bcc')}</label>
                            <div className="email-field-input-wrap">
                                <input
                                    type="text"
                                    className={`email-field-input ${(errors.bcc || errors['bcc.0']) ? 'is-invalid' : ''}`}
                                    value={bcc}
                                    onChange={(e) => { setBcc(e.target.value); clearError('bcc'); }}
                                />
                                {(errors.bcc || errors['bcc.0']) && (
                                    <div className="field-error">{(errors.bcc || errors['bcc.0'])[0]}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Subject */}
                    <div className="email-composer-field-row">
                        <label className="email-field-label">{t('pages.email.compose.subject')}</label>
                        <div className="email-field-input-wrap">
                            <input
                                type="text"
                                className={`email-field-input ${errors.subject ? 'is-invalid' : ''}`}
                                value={emailSubject}
                                onChange={(e) => { setEmailSubject(e.target.value); clearError('subject'); }}
                            />
                            {errors.subject && (
                                <div className="field-error">{errors.subject[0]}</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Rich Text Editor */}
                <div className="email-composer-body">
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={body}
                        onChange={(val) => { setBody(val); clearError('body'); }}
                        modules={modules}
                        formats={formats}
                        className={`email-quill-editor ${errors.body ? 'is-invalid' : ''}`}
                    />
                </div>
                {errors.body && (
                    <div className="field-error" style={{ padding: '4px 20px', flexShrink: 0 }}>{errors.body[0]}</div>
                )}

                {/* Footer and Attachments */}
                <div className="email-composer-footer">
                    <button
                        className="email-footer-icon-btn"
                        onClick={() => fileInputRef.current?.click()}
                        title={t('pages.email.compose.attach_files')}
                    >
                        <FaPaperclip />
                    </button>
                    <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />

                    <div className="email-attachment-chips">
                        {attachments.map((att) => (
                            <EmailAttachmentChip
                                key={att.tempId}
                                name={att.file.name}
                                size={att.file.size}
                                mimeType={att.file.type}
                                progress={att.progress}
                                error={att.error}
                                onRemove={() => removeAttachment(att)}
                            />
                        ))}
                    </div>

                    <div className="email-footer-actions">
                        <button
                            className="email-composer-btn email-draft-btn"
                            onClick={handleDraft}
                            disabled={isSending || !activeAccountId}
                            title={!activeAccountId ? t('pages.email.compose.no_account_selected') : t('pages.email.compose.save_draft')}
                        >
                            <FaSave style={{ marginRight: '8px' }} />
                            {t('pages.email.compose.save_draft')}
                        </button>
                        <button
                            className="email-composer-btn email-send-btn"
                            onClick={handleSend}
                            disabled={isSending || !activeAccountId || isUploading}
                            title={
                                !activeAccountId ? t('pages.email.compose.no_account_selected') :
                                isUploading ? t('pages.email.compose.uploads_in_progress') :
                                t('pages.email.compose.send_email_title')
                            }
                        >
                            <FaPaperPlane style={{ marginRight: '8px' }} />
                            {isSending ? t('pages.email.compose.sending') : t('pages.email.compose.send')}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EmailComposer;
